import React from 'react'
import {Route, Switch, Router as BrowserRouter} from 'react-router-dom'
import {CompatRouter} from 'react-router-dom-v5-compat'

import {
  AlbumsView,
  AlbumView,
  AudiencesView,
  CallbackView,
  CampanionStaffAccessView,
  EzTextingGroupView,
  EzTextingView,
  EZTLoginView,
  FacialRecognitionView,
  FormRemindersView,
  FrDiagnosticsView,
  IntegrationsView,
  LoginView,
  LogoutView,
  MicropostsView,
  PhotoUploaderView,
  PrivacyPolicyView,
  RegistrationLinkView,
  TaggedPhotoReportView,
  TermsOfServiceView,
} from '../views'
import {MicropostsProvider} from '../stores/MicropostsStore'
import {AuthRoute} from '../components/AuthRoute/AuthRoute'
import {history} from './history'
import {PageNotFoundView} from '../views/PageNotFoundView/PageNotFoundView'
import {sessionManager} from '../utils'
import {HomeView} from '../views/HomeView/HomeView'
import {AuthErrorView} from '../views/AuthErrorView/AuthErrorView'
import {useAuth} from '../stores/AuthStore'
import {useFeatureFlag} from '../hooks/useFeatureFlag'
import {useIsCampMinderEmployee} from '../hooks/useIsCampMinderEmployee'
import {useSessionActivity} from '../hooks/useSessionActivity'
import {
  CONFIGCAT_FLAG_KEY as FR_DIAGNOSTICS_FLAG_KEY,
  FR_DIAGNOSTICS_PATH,
} from '../views/FrDiagnosticsView/constants'
import {
  CONFIGCAT_FLAG_KEY as TAGGED_PHOTO_REPORT_FLAG_KEY,
  TAGGED_PHOTO_REPORT_PATH,
} from '../views/TaggedPhotoReportView/constants'
import {FACIAL_RECOGNITION_PATH} from '../views/FacialRecognitionView/constants'
import {useFacialRecognitionTabs} from '../views/FacialRecognitionView/useFacialRecognitionTabs'
import {
  ALBUMS_PATH,
  clearReturnPath,
  isUnauthenticatedRoute,
} from '../navigation'

/*
  FR Diagnostics is CampMinder-internal, not a client entitlement, so it can't
  use AuthRoute's `clientAuthorization` prop (which gates on a camp's features).
  Gating instead on the employee check + the ConfigCat rollout flag, and
  rendering PageNotFoundView when either is closed so the URL isn't a
  discoverable affordance for a camp admin who guesses it.

  This is a VISIBILITY gate only. The faces endpoint independently enforces the
  authoritative `campminderstaff` check and returns 403, so nothing here can
  leak data.
*/
const FrDiagnosticsRoute: React.FC = () => {
  const isCampMinderEmployee = useIsCampMinderEmployee()
  const {value: flagEnabled, loading} = useFeatureFlag(
    FR_DIAGNOSTICS_FLAG_KEY,
    false,
  )

  // Render nothing until ConfigCat resolves, so an allowed user doesn't get a
  // not-found flash before the flag lands.
  if (loading) return null
  if (!isCampMinderEmployee || !flagEnabled) return <PageNotFoundView />
  return <FrDiagnosticsView />
}

/*
  The Tagged Photo Report is CampMinder-internal too, and gated exactly like FR
  Diagnostics above and for the same reasons (D-A): AuthRoute's
  `clientAuthorization` gates on a camp's entitlements, and a camp does not
  "have" this feature. Editing AuthRoute to teach it about internal surfaces
  would change behavior app-wide on a declared high-risk path, so the gate lives
  here in a route wrapper instead.

  Not-found rather than access-denied when the gate is closed (D-C): a 403-style
  page confirms the address exists to a camp admin who guessed it.

  This is a VISIBILITY gate only. The faces coverage endpoint independently
  enforces the authoritative `campminderstaff` check and returns 403, so nothing
  here can leak data.
*/
const TaggedPhotoReportRoute: React.FC = () => {
  const isCampMinderEmployee = useIsCampMinderEmployee()
  const {value: flagEnabled, loading} = useFeatureFlag(
    TAGGED_PHOTO_REPORT_FLAG_KEY,
    false,
  )

  // Render nothing until ConfigCat resolves, so an allowed user doesn't get a
  // not-found flash before the flag lands.
  if (loading) return null
  if (!isCampMinderEmployee || !flagEnabled) return <PageNotFoundView />
  return <TaggedPhotoReportView />
}

/*
  The Facial Recognition page is Campminder-internal and gated the same way as
  FR Diagnostics above: the employee check plus ConfigCat. Each tab has its own
  flag, so the page opens when any tab flag is on. Not-found when the gate is
  closed, nothing while the flags resolve.

  This is a VISIBILITY gate only. The faces endpoints enforce the staff check
  server-side.
*/
const FacialRecognitionRoute: React.FC = () => {
  const isCampMinderEmployee = useIsCampMinderEmployee()
  const {visibleTabs, loading} = useFacialRecognitionTabs()

  if (loading) return null
  if (!isCampMinderEmployee || visibleTabs.length === 0)
    return <PageNotFoundView />
  return <FacialRecognitionView />
}

// TODO: Find a better way to let us match authenticated app calls.
// So for things like lookups that could be used in several places, but also require a authenticated user
// It needs to wrap all autheticated routes, so maybe some other kind of AuthenticateApp components
// that has the routing config for all the AuthetnicatedRotues

export const Router: React.FC<React.PropsWithChildren> = () => {
  const {logout} = useAuth()

  // Local interaction (pointer / keyboard) counts as session activity, not just
  // an axios response. Mounted here beside sessionManager.init so the two halves
  // of the idle-session policy live together (AB#24880).
  useSessionActivity()

  React.useEffect(() => {
    sessionManager.init(
      (): void => {
        if (
          history.location &&
          history.location.pathname &&
          !isUnauthenticatedRoute(history.location.pathname)
        ) {
          // Fire-and-forget: the redirect below proceeds regardless of the
          // server logout result, which `logout()` already logs on failure.
          // Catch so a rejected logout doesn't become an unhandled rejection.
          logout().catch(() => {
            // already logged inside AuthStore.logout
          })

          // The session is over, so whatever the user was headed for is stale.
          // Leaving it stashed would silently redirect their NEXT login.
          clearReturnPath()

          window.location.href = `${window.location.protocol}//${window.location.host}/login?expired=1`
        }
      },
      60,
      30,
    )
    return (): void => {
      sessionManager.cleanUp()
    }
  }, [logout])

  return (
    <BrowserRouter history={history}>
      <CompatRouter>
        <Switch>
          <Route path='/login' component={LoginView} />

          <Route path='/callback' component={CallbackView} />

          <Route path='/auth-error' component={AuthErrorView} />

          <Route path='/logout' component={LogoutView} />

          <Route path='/privacy-policy' component={PrivacyPolicyView} />

          <Route path='/terms-of-service' component={TermsOfServiceView} />

          <AuthRoute
            exact
            path='/'
            render={(): React.ReactNode => <HomeView />}
          />

          <AuthRoute
            path='/microposts'
            render={(): React.ReactNode => (
              <MicropostsProvider>
                <MicropostsView />
              </MicropostsProvider>
            )}
          />

          <AuthRoute
            path={ALBUMS_PATH}
            clientAuthorization='view-photo-uploader'
            render={(): React.ReactNode => <AlbumsView />}
          />
          <AuthRoute
            path='/album/:id'
            clientAuthorization='view-photo-uploader'
            render={(): React.ReactNode => <AlbumView />}
            exact
          />
          <AuthRoute
            path='/album/:id/upload'
            render={(): React.ReactNode => <PhotoUploaderView />}
          />
          <AuthRoute
            path='/audiences'
            render={(): React.ReactNode => <AudiencesView />}
          />
          <AuthRoute
            exact
            path='/eztexting'
            render={(): React.ReactNode => <EZTLoginView />}
          />
          <AuthRoute
            exact
            path='/integrations'
            render={(): React.ReactNode => <IntegrationsView />}
          />
          <AuthRoute
            path='/integrations/eztexting'
            exact
            render={(): React.ReactNode => <EzTextingView />}
          />
          <AuthRoute
            path='/integrations/eztexting/groups/:id'
            render={(): React.ReactNode => <EzTextingGroupView />}
          />
          <AuthRoute
            path='/home'
            render={(): React.ReactNode => <HomeView />}
          />
          <AuthRoute
            path='/formreminders'
            clientAuthorization='forms-reminder'
            render={(): React.ReactNode => <FormRemindersView />}
          />

          <AuthRoute
            path='/registration'
            clientAuthorization='admin-menu-registration'
            render={(): React.ReactNode => <RegistrationLinkView />}
          />

          {/*
            Two gates protect this surface:
              - clientAuthorization (here) is the customer entitlement check;
                only clients with `enable-campanion-staff-engagement-features`
                provisioned can reach the route.
              - The ConfigCat flag `enable-campanion-staff-access-panel`
                (checked inside the view and the side-menu helper) is the
                development rollout gate. A client may be entitled but with
                the panel still hidden during staged rollout.
          */}
          <AuthRoute
            path='/campanion-staff-access'
            clientAuthorization='enable-campanion-staff-engagement-features'
            render={(): React.ReactNode => <CampanionStaffAccessView />}
          />

          <AuthRoute
            path={FR_DIAGNOSTICS_PATH}
            render={(): React.ReactNode => <FrDiagnosticsRoute />}
          />

          <AuthRoute
            path={TAGGED_PHOTO_REPORT_PATH}
            render={(): React.ReactNode => <TaggedPhotoReportRoute />}
          />

          <AuthRoute
            path={FACIAL_RECOGNITION_PATH}
            render={(): React.ReactNode => <FacialRecognitionRoute />}
          />

          <Route path='*' component={PageNotFoundView} />
        </Switch>
      </CompatRouter>
    </BrowserRouter>
  )
}
